import { useEffect } from "react";
import { env } from "../config/env";

type SeoProps = {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: "website" | "article";
};

const DEFAULT_TITLE = "Zion Classical Academy";
const DEFAULT_DESCRIPTION = "Classical Christian education for Pre-K through 6th grade in Hastings, Nebraska.";
const DEFAULT_IMAGE = "/images/zion-classical-academy-logo.png";
const STRUCTURED_DATA_ID = "zca-structured-data";

function setMeta(selector: string, attr: "content" | "href", value: string) {
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

function titleFromSegment(segment: string) {
  return segment
    .split("-")
    .map((part) => part ? `${part[0].toUpperCase()}${part.slice(1)}` : "")
    .join(" ");
}

function buildBreadcrumbs(siteUrl: string, path: string) {
  const segments = path.split("/").filter(Boolean);
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: siteUrl,
    },
  ];

  segments.forEach((segment, index) => {
    const itemPath = `/${segments.slice(0, index + 1).join("/")}`;
    items.push({
      "@type": "ListItem",
      position: index + 2,
      name: titleFromSegment(segment),
      item: `${siteUrl}${itemPath}`,
    });
  });

  return {
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

function setStructuredData(data: unknown) {
  let script = document.getElementById(STRUCTURED_DATA_ID) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.id = STRUCTURED_DATA_ID;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.text = JSON.stringify(data);
}

export function Seo({ title, description, path = "", image = DEFAULT_IMAGE, type = "website" }: SeoProps) {
  useEffect(() => {
    const siteUrl = env.siteUrl.replace(/\/$/, "");
    const pageTitle = title && title !== DEFAULT_TITLE ? `${title} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
    const pageDescription = description || DEFAULT_DESCRIPTION;
    const currentPath = path || window.location.pathname;
    const canonical = `${siteUrl}${currentPath}`;
    const imageUrl = image.startsWith("http") ? image : `${siteUrl}${image.startsWith("/") ? image : `/${image}`}`;

    document.title = pageTitle;
    setMeta('meta[name="description"]', "content", pageDescription);
    setMeta('meta[property="og:type"]', "content", type);
    setMeta('meta[property="og:title"]', "content", pageTitle);
    setMeta('meta[property="og:description"]', "content", pageDescription);
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[property="og:image"]', "content", imageUrl);
    setMeta('meta[name="twitter:title"]', "content", pageTitle);
    setMeta('meta[name="twitter:description"]', "content", pageDescription);
    setMeta('meta[name="twitter:image"]', "content", imageUrl);
    setMeta('link[rel="canonical"]', "href", canonical);
    setStructuredData({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": ["School", "EducationalOrganization"],
          "@id": `${siteUrl}/#school`,
          name: env.schoolName,
          url: siteUrl,
          logo: imageUrl,
          image: imageUrl,
          description: DEFAULT_DESCRIPTION,
          telephone: env.schoolPhone,
          email: env.contactEmail,
          address: {
            "@type": "PostalAddress",
            streetAddress: env.schoolStreetAddress,
            addressLocality: env.schoolCity,
            addressRegion: env.schoolRegion,
            postalCode: env.schoolPostalCode,
            addressCountry: env.schoolCountry,
          },
          areaServed: [
            { "@type": "City", name: "Hastings" },
            { "@type": "State", name: "Nebraska" },
          ],
          sameAs: [env.facebookUrl].filter(Boolean),
        },
        {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          url: siteUrl,
          name: env.schoolName,
          publisher: { "@id": `${siteUrl}/#school` },
          inLanguage: "en-US",
        },
        {
          "@type": "WebPage",
          "@id": `${canonical}#webpage`,
          url: canonical,
          name: pageTitle,
          description: pageDescription,
          isPartOf: { "@id": `${siteUrl}/#website` },
          about: { "@id": `${siteUrl}/#school` },
          inLanguage: "en-US",
        },
        buildBreadcrumbs(siteUrl, currentPath),
      ],
    });
  }, [description, image, path, title, type]);

  return null;
}
